import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES, buildTelehealthSessionPath } from "@/routes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Clock, 
  Shield, 
  Video, 
  Mic, 
  MicOff, 
  VideoOff,
  CheckCircle2,
  Loader2,
  Phone,
  ArrowLeft,
  Settings,
  Volume2
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { WaitingRoomChat } from "@/components/telehealth/WaitingRoomChat";

export default function WaitingRoom() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user } = useAuth();
  
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [waitTime, setWaitTime] = useState(0);
  const [sessionStatus, setSessionStatus] = useState<string>("waiting");
  const [providerName, setProviderName] = useState("Your Provider");
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [deviceCheckComplete, setDeviceCheckComplete] = useState(false);

  // Update session status to "waiting" when member enters waiting room
  useEffect(() => {
    const updateSessionStatus = async () => {
      if (!sessionId) return;
      
      const { error } = await supabase
        .from("telehealth_sessions")
        .update({ status: "waiting" })
        .eq("id", sessionId);

      if (error) {
        console.error("Error updating session status:", error);
      }
    };

    updateSessionStatus();
  }, [sessionId]);

  // Subscribe to session status changes for real-time updates
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`session-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "telehealth_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newStatus = payload.new.status;
          setSessionStatus(newStatus);
          
          if (newStatus === "in_progress") {
            toast.success("The provider has admitted you to the session!");
            navigate(buildTelehealthSessionPath(sessionId));
          } else if (newStatus === "cancelled") {
            toast.error("The session has been cancelled");
            navigate(ROUTES.MEMBER);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, navigate]);

  // Wait time counter
  useEffect(() => {
    const interval = setInterval(() => {
      setWaitTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Simulate device check
  useEffect(() => {
    const timer = setTimeout(() => {
      setDeviceCheckComplete(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const formatWaitTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleLeaveWaitingRoom = async () => {
    if (sessionId) {
      await supabase
        .from("telehealth_sessions")
        .update({ status: "scheduled" })
        .eq("id", sessionId);
    }
    navigate(-1);
  };

  const localName = user?.email?.split("@")[0] || "You";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm px-4 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleLeaveWaitingRoom}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Leave
            </Button>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="text-lg font-semibold text-foreground">Waiting Room</h1>
              <p className="text-sm text-muted-foreground">Preparing for your session</p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1">
            <Shield className="h-3 w-3" />
            HIPAA Compliant
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl space-y-6">
          {/* Status Banner */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="rounded-full bg-primary/10 p-3 animate-pulse">
                    <Loader2 className="h-6 w-6 text-primary animate-spin" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      Waiting for provider to admit you
                    </h2>
                    <p className="text-muted-foreground">
                      {providerName} will be with you shortly
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span className="font-mono text-lg">{formatWaitTime(waitTime)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Wait time</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Video Preview */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Camera Preview</CardTitle>
                <CardDescription>Check your video before the session</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Video placeholder */}
                <div className="relative aspect-video rounded-lg bg-muted overflow-hidden">
                  {isVideoEnabled ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/10">
                      <div className="text-center">
                        <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                          <span className="text-3xl font-semibold text-primary">
                            {localName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">Camera preview</p>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <VideoOff className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Camera is off</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="flex justify-center gap-3">
                  <Button
                    variant={isVideoEnabled ? "default" : "secondary"}
                    size="lg"
                    className="gap-2"
                    onClick={() => setIsVideoEnabled(!isVideoEnabled)}
                  >
                    {isVideoEnabled ? (
                      <Video className="h-5 w-5" />
                    ) : (
                      <VideoOff className="h-5 w-5" />
                    )}
                    {isVideoEnabled ? "Camera On" : "Camera Off"}
                  </Button>
                  <Button
                    variant={isAudioEnabled ? "default" : "secondary"}
                    size="lg"
                    className="gap-2"
                    onClick={() => setIsAudioEnabled(!isAudioEnabled)}
                  >
                    {isAudioEnabled ? (
                      <Mic className="h-5 w-5" />
                    ) : (
                      <MicOff className="h-5 w-5" />
                    )}
                    {isAudioEnabled ? "Mic On" : "Mic Off"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Device Check & Tips */}
            <div className="space-y-6">
              {/* Device Check Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    System Check
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Camera</span>
                      </div>
                      {deviceCheckComplete ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="h-3 w-3 text-primary" />
                          Ready
                        </Badge>
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mic className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Microphone</span>
                      </div>
                      {deviceCheckComplete ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="h-3 w-3 text-primary" />
                          Ready
                        </Badge>
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Volume2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Speakers</span>
                      </div>
                      {deviceCheckComplete ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="h-3 w-3 text-primary" />
                          Ready
                        </Badge>
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {deviceCheckComplete && (
                    <div className="pt-2">
                      <Progress value={100} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-2">
                        All devices ready for your session
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tips Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Tips for Your Visit</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Find a quiet, well-lit space for your appointment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Have your medications list and questions ready</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Ensure your device is charged or plugged in</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Chat with Provider Card */}
              {sessionId && (
                <Card className="border-primary/20">
                  <WaitingRoomChat 
                    sessionId={sessionId} 
                    senderRole="member" 
                    className="h-64"
                  />
                </Card>
              )}
            </div>
          </div>

          {/* Bottom Info */}
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>Having trouble? Call (555) 123-4567 for technical support</span>
                </div>
                <Badge variant="secondary">
                  Session ID: {sessionId?.slice(0, 8)}...
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
